import { z } from 'zod'
import { ADMIN_GROUP_ALIAS } from './rbac.ts'

/**
 * Messaging (docs/01-product-design.md §5).
 *
 * Two thread types only: admin <-> buyer org and admin <-> seller org. There is no
 * shape in this file that could carry a buyer and a seller at once, which is the
 * cheapest possible enforcement of D4.
 *
 * Every message stores its real `senderUserId` for the audit trail, and the DTO
 * replaces it with the group alias for the counterparty. "Sita replied" internally,
 * "Support Desk" externally.
 */

export const CONVERSATION_KINDS = ['buyer', 'seller'] as const
export type ConversationKind = (typeof CONVERSATION_KINDS)[number]
export const conversationKindSchema = z.enum(CONVERSATION_KINDS)

export const MESSAGE_KINDS = ['text', 'offer_card', 'system', 'internal_note'] as const
export type MessageKind = (typeof MESSAGE_KINDS)[number]
export const messageKindSchema = z.enum(MESSAGE_KINDS)

export const conversationSchema = z.object({
  id: z.string(),
  kind: conversationKindSchema,
  subject: z.string(),
  status: z.enum(['open', 'closed']),
  /**
   * Admin-facing only — the counterparty already knows who it is. The counterparty
   * is a business or a customer with none (D15); `partyName` is whichever to show.
   */
  orgId: z.string().nullable(),
  userId: z.string().nullable(),
  partyName: z.string().nullable(),
  assignedToUserId: z.string().nullable(),
  assignedToName: z.string().nullable(),
  rfqId: z.string().nullable(),
  rfqCode: z.string().nullable(),
  orderId: z.string().nullable(),
  orderCode: z.string().nullable(),
  lastMessageAt: z.number().int().nullable(),
  lastMessagePreview: z.string().nullable(),
  unreadCount: z.number().int(),
  /** How long the oldest unanswered counterparty message has been waiting, in ms. */
  waitingMs: z.number().int().nullable(),
  createdAt: z.number().int(),
  version: z.number().int(),
})
export type Conversation = z.infer<typeof conversationSchema>

/**
 * What stands in for a sender's name. `desk` is the alias itself — one voice for
 * the whole desk (D8) — and the other three are the app speaking about a person
 * rather than naming them, which is why they are labels and not identities.
 */
export const SENDER_LABELS = {
  update: 'Update',
  you: 'You',
  desk: ADMIN_GROUP_ALIAS,
  customer: 'Customer',
} as const
export type SenderLabelKey = keyof typeof SENDER_LABELS

export const messageSchema = z.object({
  id: z.string(),
  conversationId: z.string(),
  kind: messageKindSchema,
  /** "Support Desk", the shop's name, or "You". Never an individual admin's name. */
  senderLabel: z.string(),
  mine: z.boolean(),
  fromDesk: z.boolean(),
  body: z.string().nullable(),
  offerId: z.string().nullable(),
  attachmentAssetId: z.string().nullable(),
  createdAt: z.number().int(),
})
export type Message = z.infer<typeof messageSchema>

export const sendMessageSchema = z
  .object({
    body: z.string().trim().max(4000).nullish(),
    /**
     * Admin-only. Same thread, invisible to the counterparty — so "seller B quoted
     * lower, push for 5% more" lives with the conversation instead of in a side
     * channel where it gets lost.
     */
    internal: z.boolean().default(false),
    attachmentAssetId: z.string().max(64).nullish(),
  })
  .refine((v) => Boolean(v.body?.trim()) || Boolean(v.attachmentAssetId), {
    message: 'Write a message or attach a file.',
    path: ['body'],
  })
export type SendMessageInput = z.infer<typeof sendMessageSchema>

export const startConversationSchema = z.object({
  /**
   * Admin starts a thread with a counterparty; a counterparty starts one with the
   * desk. For the desk that counterparty is a business, or — for a customer who has
   * no business (D15) — the person themselves. Exactly one of the two.
   */
  orgId: z.string().max(64).nullish(),
  userId: z.string().max(64).nullish(),
  subject: z.string().trim().min(2, 'Give it a subject.').max(120),
  rfqId: z.string().max(64).nullish(),
  orderId: z.string().max(64).nullish(),
  body: z.string().trim().max(4000).nullish(),
})
export type StartConversationInput = z.infer<typeof startConversationSchema>

export const assignConversationSchema = z.object({
  /** Null unassigns, putting the thread back in the shared queue. */
  userId: z.string().max(64).nullable(),
})
export type AssignConversationInput = z.infer<typeof assignConversationSchema>

/**
 * Poll cadence for an open thread. Fast enough to feel live, slow enough that a
 * hundred open tabs cost nothing — and the client stops entirely when hidden.
 * See docs/02-architecture.md §6 for why this is not a WebSocket yet.
 */
export const CHAT_POLL_MS = 4000
export const INBOX_POLL_MS = 30_000

export const CANNED_REPLIES: readonly { label: string; body: string }[] = [
  {
    label: 'Checking with shops',
    body: "Thanks — we're checking prices with our shops now and will come back to you shortly.",
  },
  {
    label: 'Need the quantity',
    body: 'Could you confirm the quantity you need? The rate changes with volume.',
  },
  {
    label: 'Best we can do',
    body: "That's the best rate we can do on this quantity. Order more and the price drops again.",
  },
  {
    label: 'Delivery timing',
    body: 'We can deliver within the lead time shown. Tell us the site and a time that suits you.',
  },
]
